import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Loader2, XCircle, Download,
} from 'lucide-react';
import { CenterMorphModal, CenterMorphModalContent } from './beui/CenterMorphModal';
import { Button } from './ui/button';
import { supabase } from '../lib/supabaseClient';
import { useRenderProfiler, measureAsync } from '../lib/perfMonitor';

// Header lajur yang dikenali (tidak kira huruf besar/kecil)
const COLS = {
  nama: ['nama penuh', 'nama lengkap', 'full name', 'nama'],
  grup: ['grup', 'group'],
  no_pelajar: ['no. pelajar', 'no pelajar', 'no. siswa', 'student no', 'no siswa'],
  kelas: ['kelas', 'class'],
  tahun: ['tahun ajaran', 'tahun', 'year'],
  no_kp: ['no. kp', 'no kp', 'no. identitas', 'id no', 'no identitas'],
  telefon: ['telefon orangtua', 'telefon', 'no. telepon', 'phone', 'no telefon'],
  status: ['status'],
};

const MAX_BYTES = 2 * 1024 * 1024;

function normHeader(h) {
  return String(h || '').trim().toLowerCase().replace(/\*/g, '').replace(/\s+/g, ' ').trim();
}

function mapRow(obj) {
  const entries = Object.entries(obj);
  const get = (keys) => {
    for (const [h, v] of entries) {
      if (keys.includes(normHeader(h))) return String(v ?? '').trim();
    }
    return '';
  };
  return {
    nama: get(COLS.nama),
    grup: get(COLS.grup),
    no_pelajar: get(COLS.no_pelajar),
    kelas: get(COLS.kelas),
    tahun: get(COLS.tahun),
    no_kp: get(COLS.no_kp),
    telefon: get(COLS.telefon),
    status: get(COLS.status),
  };
}

function parseCsv(text) {
  const clean = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows = [];
  let cur = [''], inQ = false, field = '';
  // parse mudah dengan sokongan quote
  const lines = [];
  let line = '';
  for (let i = 0; i < clean.length; i++) {
    const ch = clean[i];
    if (ch === '"') { inQ = !inQ; line += ch; }
    else if (ch === '\n' && !inQ) { lines.push(line); line = ''; }
    else line += ch;
  }
  if (line) lines.push(line);
  const splitLine = (l) => {
    const out = []; let f = '', q = false;
    for (let i = 0; i < l.length; i++) {
      const ch = l[i];
      if (ch === '"') {
        if (q && l[i + 1] === '"') { f += '"'; i++; } else q = !q;
      } else if (ch === ',' && !q) { out.push(f); f = ''; }
      else f += ch;
    }
    out.push(f);
    return out.map((s) => s.trim());
  };
  if (lines.length < 2) return [];
  const headers = splitLine(lines[0]);
  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;
    const vals = splitLine(lines[i]);
    const obj = {};
    headers.forEach((h, idx) => { obj[h] = vals[idx] ?? ''; });
    rows.push(obj);
  }
  void cur;
  return rows;
}

export default function StudentImportDialog({ open, onOpenChange, school, onDone }) {
  const { t } = useTranslation();
  const [step, setStep] = useState('pick'); // pick | preview | done
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const [classMap, setClassMap] = useState({});
  const [yearMap, setYearMap] = useState({});
  const [groupMap, setGroupMap] = useState({});
  const fileRef = useRef(null);

  useRenderProfiler('StudentImportDialog', { step, rowCount: rows?.length || 0 });

  const reset = () => {
    setStep('pick'); setRows([]); setErr(''); setResult(null); setBusy(false);
  };

  const close = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const loadLookups = async () => {
    const schoolId = school?.id;
    const [{ data: classes }, { data: years }, { data: groups, error: gErr }] = await Promise.all([
      supabase.from('classes').select('id, nama').eq('school_id', schoolId),
      supabase.from('school_years').select('id, tahun').eq('school_id', schoolId),
      supabase.from('groups').select('id, nama, class_id').eq('school_id', schoolId),
    ]);
    const cm = {};
    (classes || []).forEach((c) => { cm[c.nama.trim().toLowerCase()] = c.id; });
    const ym = {};
    (years || []).forEach((y) => { ym[String(y.tahun)] = y.id; });
    // Jika jadual groups belum wujud (migrasi 007 belum dijalankan), langkau semakan grup
    const gm = gErr ? null : {};
    if (gm) (groups || []).forEach((g) => { gm[`${g.class_id}||${g.nama.trim().toLowerCase()}`] = g.id; });
    setClassMap(cm); setYearMap(ym); setGroupMap(gm);
    return { cm, ym, gm };
  };

  const validate = (mapped, cm, ym, gm) => mapped.map((r) => {
    const errors = [];
    if (!r.nama) errors.push(t('ralatNamaWajib'));
    let class_id = null;
    if (r.kelas) {
      class_id = cm[r.kelas.trim().toLowerCase()] || null;
      if (!class_id) errors.push(t('ralatKelasTiada', { v: r.kelas }));
    }
    let group_id = null;
    if (r.grup && class_id && gm) {
      group_id = gm[`${class_id}||${r.grup.trim().toLowerCase()}`] || null;
      if (!group_id) errors.push(t('ralatGrupTiada', { v: r.grup }));
    } else if (r.grup && !class_id) {
      errors.push(t('ralatGrupTiadaKelas', { v: r.grup }));
    }
    let school_year_id = null;
    if (r.tahun) {
      school_year_id = ym[r.tahun.trim()] || null;
      if (!school_year_id) errors.push(t('ralatTahunTiada', { v: r.tahun }));
    }
    const st = (r.status || '').toLowerCase();
    const aktif = !st || (!/tidak|non|inactive/.test(st) && /aktif|active/i.test(st));
    return { ...r, class_id, group_id, school_year_id, aktif, errors, ok: errors.length === 0 };
  });

  const handleFile = async (file) => {
    setErr('');
    if (!file) return;
    if (file.size > MAX_BYTES) { setErr(t('failTerlaluBesar')); return; }
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) { setErr(t('formatDisokong')); return; }
    setBusy(true);
    await measureAsync(`Excel/CSV Parse (${file.name})`, async () => {
      try {
        let raw;
        if (ext === 'csv') {
          raw = parseCsv(await file.text());
        } else {
          const XLSX = await import('xlsx');
          const buf = await file.arrayBuffer();
          const wb = XLSX.read(buf, { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          // Cari baris header sebenar (template ada 3 baris tajuk sebelum header)
          const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
          let hIdx = aoa.findIndex((row) => Array.isArray(row) && row.some((c) => COLS.nama.includes(normHeader(c))));
          if (hIdx === -1) hIdx = 0;
          const headers = aoa[hIdx];
          raw = aoa.slice(hIdx + 1)
            .filter((row) => Array.isArray(row) && row.some((c) => String(c ?? '').trim() !== ''))
            .map((row) => {
              const obj = {};
              headers.forEach((h, i) => { obj[h] = row[i] ?? ''; });
              return obj;
            });
        }
        const mapped = raw.map(mapRow).filter((r) => r.nama || r.no_kp || r.no_pelajar);
        if (mapped.length === 0) { setErr(t('pilihFailDulu')); setBusy(false); return; }
        const { cm, ym, gm } = await loadLookups();
        setRows(validate(mapped, cm, ym, gm));
        setStep('preview');
      } catch (e) {
        setErr(t('ralat'));
      }
    }, { fileSize: `${(file.size / 1024).toFixed(1)} KB`, format: ext });
    setBusy(false);
  };

  const doImport = async () => {
    const valid = rows.filter((r) => r.ok);
    if (valid.length === 0) return;
    setBusy(true);
    await measureAsync(`Bulk Student Insert (${valid.length} pelajar)`, async () => {
      let okCount = 0;
      const failed = [];
      let firstError = '';
      const BATCH = 50;
      for (let i = 0; i < valid.length; i += BATCH) {
        const batch = valid.slice(i, i + BATCH).map((r) => ({
          school_id: school.id,
          nama: r.nama,
          no_pelajar: r.no_pelajar || null,
          no_kp: r.no_kp || '',
          class_id: r.class_id,
          group_id: r.group_id || null,
          school_year_id: r.school_year_id,
          telefon_orangtua: r.telefon || null,
          aktif: r.aktif,
        }));
        const { error } = await supabase.from('students').insert(batch);
        if (error) {
          // Batch gagal (cth. 1 baris bermasalah) — cuba semula baris demi baris
          // supaya baris yang sah tidak ikut gagal.
          for (const b of batch) {
            const { error: e1 } = await supabase.from('students').insert(b);
            if (e1) {
              if (!firstError) firstError = e1.message || String(e1);
              failed.push(b.nama);
            } else {
              okCount += 1;
            }
          }
        } else {
          okCount += batch.length;
        }
      }
      setResult({ ok: okCount, fail: failed.length, firstError });
      setStep('done');
      onDone && onDone();
    }, { totalRecords: valid.length });
    setBusy(false);
  };

  const validCount = rows.filter((r) => r.ok).length;
  const errCount = rows.length - validCount;

  return (
    <CenterMorphModal open={open} onOpenChange={close}>
      <CenterMorphModalContent ariaLabel={t('importPelajar')} className="max-w-2xl bg-card" closeButtonLabel={t('tutupModal')}>
        <div className="p-6 max-h-[85vh] overflow-y-auto">
          <div className="mb-4 pr-8">
            <h2 className="text-lg font-extrabold text-foreground">{t('importPelajar')}</h2>
            <p className="text-xs text-muted-foreground mt-1">{t('lajurWajib')} · {t('formatDisokong')}</p>
          </div>

          {step === 'pick' && (
            <div>
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
                className={`flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-12 text-center cursor-pointer transition-colors ${dragOver ? 'border-brand bg-brand-light' : 'border-border hover:border-brand/50 hover:bg-muted'}`}
                data-testid="import-dropzone"
              >
                {busy ? <Loader2 className="h-10 w-10 text-brand animate-spin" /> : <Upload className="h-10 w-10 text-muted-foreground" />}
                <p className="text-sm font-semibold text-foreground">{t('seretLetakFail')}</p>
                <p className="text-xs text-muted-foreground">{t('formatDisokong')}</p>
              </div>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }} data-testid="import-file" />
              {err && <p className="mt-3 text-sm text-red-600 dark:text-red-400 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" />{err}</p>}
              <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                <Download className="h-4 w-4" />
                <span>{t('muatTurunTemplate')}:</span>
                <a href="/templates/template-import-pelajar.xlsx" download className="font-semibold text-brand hover:underline">.xlsx</a>
                <span>·</span>
                <a href="/templates/template-import-pelajar.csv" download className="font-semibold text-brand hover:underline">.csv</a>
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div>
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 px-3 py-1 text-xs font-bold">
                  <CheckCircle2 className="h-3.5 w-3.5" />{t('barisSah', { n: validCount })}
                </span>
                {errCount > 0 && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 px-3 py-1 text-xs font-bold">
                    <XCircle className="h-3.5 w-3.5" />{t('barisRalat', { n: errCount })}
                  </span>
                )}
              </div>
              <div className="rounded-xl border border-border overflow-hidden">
                <div className="overflow-x-auto max-h-[40vh] overflow-y-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="sticky top-0 bg-muted border-b border-border">
                      <tr className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        <th className="px-4 py-2.5">{t('nama')}</th>
                        <th className="px-4 py-2.5">{t('grup')}</th>
                        <th className="px-4 py-2.5">{t('noKp')}</th>
                        <th className="px-4 py-2.5">{t('kelas')}</th>
                        <th className="px-4 py-2.5">{t('status')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {rows.map((r, i) => (
                        <tr key={i} className={r.ok ? '' : 'bg-red-50 dark:bg-red-950/60'}>
                          <td className="px-4 py-2.5">
                            <p className="font-semibold text-foreground">{r.nama || '—'}</p>
                            {r.no_pelajar && <p className="text-xs text-muted-foreground">{r.no_pelajar}</p>}
                            {r.errors.map((e, j) => <p key={j} className="text-xs text-red-600 dark:text-red-400">{e}</p>)}
                          </td>
                          <td className="px-4 py-2.5 text-muted-foreground">{r.grup || '—'}</td>
                          <td className="px-4 py-2.5 text-muted-foreground font-mono text-xs">{r.no_kp || '—'}</td>
                          <td className="px-4 py-2.5 text-muted-foreground">{r.kelas || '—'}</td>
                          <td className="px-4 py-2.5">
                            {r.ok
                              ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                              : <XCircle className="h-4 w-4 text-red-500" />}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <Button variant="outline" onClick={() => setStep('pick')} disabled={busy} className="flex-1 h-11">{t('kembali')}</Button>
                <Button onClick={doImport} disabled={busy || validCount === 0} className="flex-1 h-11 bg-brand text-white hover:bg-brand-royal" data-testid="import-confirm">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
                  {busy ? t('mengimport') : t('importSekarang')}
                </Button>
              </div>
            </div>
          )}

          {step === 'done' && result && (
            <div className="text-center py-6">
              <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
              <p className="text-lg font-bold text-foreground">{t('importBerjaya', { n: result.ok })}</p>
              {result.fail > 0 && <p className="text-sm text-red-600 dark:text-red-400 mt-1">{t('importGagal', { n: result.fail })}</p>}
              {result.firstError && (
                <p className="mt-3 mx-auto max-w-md rounded-xl bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 px-4 py-2.5 text-xs text-red-700 dark:text-red-300 font-mono break-words text-left">
                  {result.firstError}
                </p>
              )}
              <Button onClick={() => close(false)} className="mt-6 h-11 px-8 bg-brand text-white hover:bg-brand-royal">{t('tutup')}</Button>
            </div>
          )}
        </div>
      </CenterMorphModalContent>
    </CenterMorphModal>
  );
}
